import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { type UserRoleLightDto } from "@shared/types/common";
import { type MergedOwner } from "@shared/types/common/owners";
import { useSubscriptionsStore } from "@shared/stores/subscriptions";
import { useAuthStore } from "@shared/stores/auth";
import { subscriptionsService } from "@shared/services/subscriptions";
import { useNotifications } from "@shared/module-transformers/useNotifications";
import logger from "@shared/utils/logger";
import {
  clearSsoBank,
  createSsoBank,
  fillSsoWindow,
  hoistOwner,
  isListedSsoOwner,
  contiguousSsoRows,
  listedUserTotal,
  OWNERS_FETCH_MAX,
  ownersChunkKey,
  shouldApplyOwnersChunk,
  sliceUsersPage,
  ssoChunkRequest,
  takeVisibleSsoRows,
} from "@shared/stores/usersTablePage";

const PAGE_SIZE = 25;

export const useOwnersStore = defineStore("owners", () => {
  const subscriptionsStore = useSubscriptionsStore();
  const authStore = useAuthStore();

  // Internal state
  const _ssoOwners = ref<ReadonlyArray<UserRoleLightDto>>([]);
  const _ssoTotalCount = ref(0);
  const _isInitialLoading = ref(false);
  const _isPageLoading = ref(false);
  const _searchQuery = ref("");
  const _currentUserIsSso = ref(false);
  const _currentUserSsoLoading = ref(false);
  const _ssoSearch = ref<string | null>(null);
  const _listedTotal = ref<number | null>(null);
  const tableBank = createSsoBank<UserRoleLightDto>();
  let firstPageTask: Promise<void> | null = null;
  const chunkTasks = new Map<string, Promise<void>>();

  const isLoggedInUser = (email: string | null | undefined): boolean => {
    const currentEmail = authStore.user?.email?.toLowerCase();
    return email?.toLowerCase() === currentEmail;
  };

  // Computed getters
  const primaryAdmin = computed((): MergedOwner | null => {
    const admin = subscriptionsStore.primaryAdmin;
    if (!admin) {
      return null;
    }
    return {
      ...admin,
      isSsoOwner: isLoggedInUser(admin.email) && _currentUserIsSso.value,
    };
  });

  const subscriptionOwners = computed((): ReadonlyArray<UserRoleLightDto> => {
    const subscription = subscriptionsStore.currentSubscription;
    if (!subscription) {
      return [];
    }
    return subscription.owners
      .filter((owner) => owner.role !== "MYJFROG_PRIMARY_ADMIN")
      .filter((owner) => owner.role !== "MYJFROG_ADMIN_READONLY")
      .filter(
        (owner) => !owner.representsDomain && !owner.representsOrganization,
      );
  });

  const filteredSubscriptionOwners = computed(
    (): ReadonlyArray<MergedOwner> => {
      const query = _searchQuery.value.toLowerCase().trim();
      const owners = subscriptionOwners.value.map(
        (owner): MergedOwner => ({
          ...owner,
          isSsoOwner: isLoggedInUser(owner.email) && _currentUserIsSso.value,
        }),
      );
      if (!query) {
        return owners;
      }
      return owners.filter((owner) =>
        owner.email.toLowerCase().includes(query),
      );
    },
  );

  const filteredSsoOwners = computed((): ReadonlyArray<MergedOwner> => {
    const subscriptionEmails = new Set(
      subscriptionOwners.value.map((owner) => owner.email.toLowerCase()),
    );
    const adminEmail = subscriptionsStore.primaryAdmin?.email?.toLowerCase();
    if (adminEmail) {
      subscriptionEmails.add(adminEmail);
    }

    return _ssoOwners.value
      .filter(
        (owner) => !owner.representsDomain && !owner.representsOrganization,
      )
      .filter((owner) => !subscriptionEmails.has(owner.email.toLowerCase()))
      .map(
        (owner): MergedOwner => ({
          ...owner,
          isSsoOwner: true,
        }),
      );
  });

  const mergedOwners = computed((): ReadonlyArray<MergedOwner> => {
    const result: MergedOwner[] = [];
    if (primaryAdmin.value) {
      const query = _searchQuery.value.toLowerCase().trim();
      if (!query || primaryAdmin.value.email?.toLowerCase().includes(query)) {
        result.push(primaryAdmin.value);
      }
    }
    result.push(...filteredSubscriptionOwners.value);
    result.push(...filteredSsoOwners.value);
    return result;
  });

  const hasMore = computed(
    (): boolean => _ssoOwners.value.length < _ssoTotalCount.value,
  );

  const totalCount = computed((): number => {
    if (_listedTotal.value !== null) {
      return _listedTotal.value;
    }
    if (!hasMore.value) {
      return mergedOwners.value.length;
    }
    const primaryAdminCount = primaryAdmin.value ? 1 : 0;
    return (
      _ssoTotalCount.value +
      filteredSubscriptionOwners.value.length +
      primaryAdminCount
    );
  });

  const isInitialLoading = computed((): boolean => _isInitialLoading.value);
  const isPageLoading = computed((): boolean => _isPageLoading.value);
  const currentUserSsoLoading = computed(
    (): boolean => _currentUserSsoLoading.value,
  );

  const searchQuery = computed({
    get: () => _searchQuery.value,
    set: (value: string) => {
      _searchQuery.value = value;
    },
  });

  // Actions
  const checkCurrentUserSso = async (): Promise<void> => {
    const sub = subscriptionsStore.currentSubscription;
    const email = authStore.user?.email?.toLowerCase();
    if (!sub || !email) {
      return;
    }
    const subOwner = sub.owners.find((o) => o.email.toLowerCase() === email);
    if (!subOwner) {
      return;
    }

    _currentUserSsoLoading.value = true;
    try {
      const response = await subscriptionsService.fetchOwners(
        sub.accountNumber,
        0,
        PAGE_SIZE,
        email,
      );
      const match = response.data.find((o) => o.email.toLowerCase() === email);
      _currentUserIsSso.value = match?.role === subOwner.role;
    } catch (e) {
      logger.error("Failed to verify SSO status for current user", e);
      await useNotifications().error(
        "Failed to verify SSO status for current user.",
      );
      _currentUserIsSso.value = false;
    } finally {
      _currentUserSsoLoading.value = false;
    }
  };

  const localOwnersForTable = (): MergedOwner[] => {
    const rows: MergedOwner[] = [];
    const admin = primaryAdmin.value;
    const query = _searchQuery.value.toLowerCase().trim();
    if (admin && (!query || admin.email?.toLowerCase().includes(query))) {
      rows.push(admin);
    }
    rows.push(...filteredSubscriptionOwners.value);
    return hoistOwner(rows, authStore.user?.email ?? "");
  };

  const knownSsoTotal = (search: string): number | null => {
    if (tableBank.search === search && tableBank.total !== null) {
      return tableBank.total;
    }
    if (_ssoSearch.value === search) {
      return _ssoTotalCount.value;
    }
    return null;
  };

  const storeSsoChunk = (
    search: string,
    offset: number,
    rows: readonly UserRoleLightDto[],
    total: number,
  ): void => {
    if (offset === 0) {
      tableBank.search = search;
      tableBank.slots = [];
      tableBank.total = null;
      _ssoOwners.value = rows;
    } else if (offset === _ssoOwners.value.length) {
      _ssoOwners.value = [..._ssoOwners.value, ...rows];
    }
    fillSsoWindow(tableBank, search, offset, rows, total);
    _ssoTotalCount.value = total;
    _ssoSearch.value = search;
  };

  const requestOwnersChunk = (
    accountNumber: number,
    search: string,
    offset: number,
  ): Promise<void> => {
    const key = ownersChunkKey(accountNumber, search, offset);
    const existing = chunkTasks.get(key);
    if (existing) {
      return existing;
    }

    const task = fetchOwnersChunk(accountNumber, search, offset).finally(() => {
      if (chunkTasks.get(key) === task) {
        chunkTasks.delete(key);
      }
    });
    chunkTasks.set(key, task);
    return task;
  };

  const fetchOwnersChunk = async (
    accountNumber: number,
    search: string,
    offset: number,
  ): Promise<void> => {
    const response = await subscriptionsService.fetchOwners(
      accountNumber,
      offset,
      OWNERS_FETCH_MAX,
      search || undefined,
    );
    const currentAccount =
      subscriptionsStore.currentSubscription?.accountNumber;
    if (!shouldApplyOwnersChunk(accountNumber, currentAccount)) {
      return;
    }
    storeSsoChunk(search, offset, response.data, response.totalCount);
  };

  const loadOwnersChunk = async (
    search: string,
    offset: number,
  ): Promise<void> => {
    const accountNumber = subscriptionsStore.currentSubscription?.accountNumber;
    if (!accountNumber) {
      useNotifications().error("Failed to load owners.");
      throw new Error("Missing account number");
    }

    try {
      await requestOwnersChunk(accountNumber, search, offset);
    } catch (e) {
      logger.error("Failed to fetch owners page", e);
      useNotifications().error("Failed to load owners.");
      throw e;
    }
  };

  const loadVisibleSsoRows = async (
    search: string,
    offset: number,
    limit: number,
    isVisible: (row: UserRoleLightDto) => boolean,
  ): Promise<UserRoleLightDto[]> => {
    if (limit <= 0) {
      return [];
    }

    for (let attempt = 0; attempt < 50; attempt += 1) {
      const rawRows = contiguousSsoRows(tableBank, search);
      const decision = takeVisibleSsoRows({
        rawRows,
        rawTotal: knownSsoTotal(search),
        offset,
        limit,
        isVisible,
      });
      if (decision.status === "ready") {
        return decision.rows;
      }

      const request = ssoChunkRequest({
        loaded: rawRows.length,
        neededOffset: decision.offset,
        rawTotal: knownSsoTotal(search),
      });
      if (!request) {
        break;
      }
      await loadOwnersChunk(search, request.offset);
      if (contiguousSsoRows(tableBank, search).length === rawRows.length) {
        break;
      }
    }

    const fallback = takeVisibleSsoRows({
      rawRows: contiguousSsoRows(tableBank, search),
      rawTotal: knownSsoTotal(search),
      offset,
      limit,
      isVisible,
    });
    if (fallback.status === "ready") {
      return fallback.rows;
    }
    logger.error("Failed to resolve users page");
    useNotifications().error("Failed to load owners.");
    throw new Error("Failed to resolve users page");
  };

  const loadFirstPage = async (): Promise<void> => {
    const search = _searchQuery.value.trim();
    if (tableBank.search !== search) {
      clearSsoBank(tableBank);
    }
    _listedTotal.value = null;
    _ssoSearch.value = null;

    const accountNumber = subscriptionsStore.currentSubscription?.accountNumber;
    if (!accountNumber) {
      return;
    }

    _isInitialLoading.value = true;

    try {
      await requestOwnersChunk(accountNumber, search, 0);
    } catch (e) {
      logger.error("Failed to fetch SSO owners", e);
      useNotifications().error(
        "Failed to load SSO owners. Showing subscription owners only.",
      );
      _ssoOwners.value = [];
      _ssoTotalCount.value = 0;
      _ssoSearch.value = search;
      clearSsoBank(tableBank);
    } finally {
      _isInitialLoading.value = false;
    }
  };

  const fetchFirstPage = async (): Promise<void> => {
    const task = loadFirstPage();
    firstPageTask = task;
    try {
      await task;
    } finally {
      if (firstPageTask === task) {
        firstPageTask = null;
      }
    }
  };

  const fetchUsersPage = async ({
    first,
    rows,
  }: {
    first: number;
    rows: number;
  }): Promise<{ data: MergedOwner[]; totalRecords: number }> => {
    const search = _searchQuery.value.trim();
    if (firstPageTask) {
      await firstPageTask;
    }
    const localRows = localOwnersForTable();

    const pageSlice = sliceUsersPage(localRows, first, rows);
    const localEmails = new Set(
      localRows.map((row) => row.email.toLowerCase()),
    );
    const isVisibleSsoRow = (row: UserRoleLightDto) =>
      isListedSsoOwner(row, localEmails);

    if (pageSlice.ssoLimit === 0 && knownSsoTotal(search) === null) {
      await loadOwnersChunk(search, 0);
    }

    const visibleSsoRows = await loadVisibleSsoRows(
      search,
      pageSlice.ssoOffset,
      pageSlice.ssoLimit,
      isVisibleSsoRow,
    );
    const rawRows = contiguousSsoRows(tableBank, search);
    const totalRecords = listedUserTotal({
      localCount: localRows.length,
      visibleSsoCount: rawRows.filter(isVisibleSsoRow).length,
      rawLoaded: rawRows.length,
      rawTotal: knownSsoTotal(search),
    });
    if (totalRecords !== null) {
      _listedTotal.value = totalRecords;
    }

    return {
      data: [
        ...pageSlice.localSlice,
        ...visibleSsoRows.map(
          (row): MergedOwner => ({
            ...row,
            isSsoOwner: true,
          }),
        ),
      ],
      totalRecords: totalRecords ?? localRows.length,
    };
  };

  const clearUsersTableBank = (): void => {
    clearSsoBank(tableBank);
    _listedTotal.value = null;
  };

  const fetchNextPage = async (): Promise<void> => {
    if (_isPageLoading.value || !hasMore.value) {
      return;
    }

    const accountNumber = subscriptionsStore.currentSubscription?.accountNumber;
    if (!accountNumber) {
      return;
    }

    _isPageLoading.value = true;
    const nextOffset = _ssoOwners.value.length;

    try {
      const search = _searchQuery.value.trim();
      await requestOwnersChunk(accountNumber, search, nextOffset);
    } catch (e) {
      logger.error("Failed to fetch next page of SSO owners", e);
      useNotifications().error("Failed to load more owners.");
    } finally {
      _isPageLoading.value = false;
    }
  };

  const resetAndRefetch = async (): Promise<void> => {
    chunkTasks.clear();
    _ssoOwners.value = [];
    _ssoTotalCount.value = 0;
    _searchQuery.value = "";
    _ssoSearch.value = null;
    _currentUserIsSso.value = false;
    clearSsoBank(tableBank);
    _listedTotal.value = null;
    await fetchFirstPage();
  };

  return {
    mergedOwners,
    totalCount,
    isInitialLoading,
    isPageLoading,
    currentUserSsoLoading,
    hasMore,
    searchQuery,
    primaryAdmin,
    filteredSubscriptionOwners,
    filteredSsoOwners,
    fetchFirstPage,
    fetchNextPage,
    fetchUsersPage,
    clearUsersTableBank,
    resetAndRefetch,
    checkCurrentUserSso,
  };
});
